import React, { useState } from 'react';
import SubtitleRow from './SubtitleRow';
import WordForWordPreview from './WordForWordPreview';
import AnnotationRow from './AnnotationRow';
import AddWordTo, { AddWordButton, targetName } from './AddWordTo';
import { reorderHandlers } from './reorder';
import { formatTime } from './momentTime';
import './SubtitlePanel.css';
import './MomentAnnotator.css';
import './CurrentMoment.css';

import { isShowing, stepTargets, endOf, windowOfAnnotations, bunches } from '../../utils/timeline';
import Tooltip from '../Tooltip';
import Icon from '../Icon';

/**
 * CurrentMoment — the "Now" panel, right below the video on the annotate
 * page: the subtitle showing at the current time and the annotations whose
 * window covers it, each with edit and delete. A row open for editing stays
 * while the video moves on, until it is saved or cancelled.
 *
 * Its "+" annotates another word of what is showing: the subtitle, else the
 * annotations' window (Requirement 57). The form keeps that window while
 * the video moves on, until it is closed.
 *
 * Props:
 *   videoId, currentTime, subtitles, annotations — from useVideoContent
 *   language, yasna, yasnaError
 *   onSubtitleSelect(subtitle), onSubtitleEdited(updated, moved), onSubtitleDeleted(id)
 *   onAnnotationSaved(annotation), onAnnotationUpdated(annotation), onAnnotationDeleted(id)
 */
function CurrentMoment({
  videoId, currentTime, subtitles = [], annotations = [], language, yasna, yasnaError,
  onSubtitleSelect, onSubtitleEdited, onSubtitleDeleted, onAnnotationSaved, onAnnotationUpdated, onAnnotationDeleted,
}) {
  // The window a word is being added to, fixed when "+" was tapped.
  const [adding, setAdding] = useState(null);
  const [held, setHeld] = useState(() => new Set());
  const hold = (id) => (editing) => setHeld((prev) => {
    if (editing === prev.has(id)) return prev;
    const next = new Set(prev);
    if (editing) next.add(id); else next.delete(id);
    return next;
  });

  // One subtitle at a time, with its annotations: an item without an end
  // lasts until the next subtitle starts (src/utils/timeline.js).
  const showing = (item) => isShowing(item, currentTime, subtitles, annotations);
  const step = stepTargets(currentTime, subtitles, annotations);
  const seek = (t) => { if (t !== null && window.__hojaVideoSeekTo) window.__hojaVideoSeekTo(t); };

  const subs = subtitles.filter((s) => showing(s) || held.has(`s:${s.id}`));
  // In their bunch's order (Requirement 58), each movable within its bunch.
  const place = new Map();
  const moves = {};
  const bunchList = bunches(subtitles, annotations);
  bunchList.forEach((b) => {
    b.annotations.forEach((a, i) => place.set(a.id, [b.startTime, i]));
    if (b.annotations.length > 1) Object.assign(moves, reorderHandlers(b.annotations, onAnnotationUpdated));
  });
  const byPlace = (a, b) => {
    const [pa, pb] = [place.get(a.id) || [0, 0], place.get(b.id) || [0, 0]];
    return (pa[0] - pb[0]) || (pa[1] - pb[1]);
  };
  const anns = annotations.filter((a) => showing(a) || held.has(`a:${a.id}`)).sort(byPlace);
  const shownSub = subtitles.find(showing);
  const shownAnns = annotations.filter(showing);
  const target = shownSub
    ? { start: shownSub.startTime, end: endOf(shownSub), subtitle: true }
    : shownAnns.length > 0 ? { ...windowOfAnnotations(shownAnns), subtitle: false } : null;

  return (
    <section className="current-moment" aria-label="Now">
      <div className="current-moment-head">
        <h3 className="current-moment-title">Now</h3>
        <span className="current-moment-time">{formatTime(currentTime)}</span>
        {/* Jump to the previous / next subtitle or annotation */}
        <span className="current-moment-step">
          {target && (
            <AddWordButton what={targetName(target)} disabled={!!adding} onClick={() => setAdding(target)} />
          )}
          <Tooltip label={step.prev === null ? 'Nothing before' : `Previous (${formatTime(step.prev)})`}>
            <button type="button" className="icon-btn icon-btn--sm" disabled={step.prev === null}
              onClick={() => seek(step.prev)} aria-label="Previous subtitle or annotation">
              <Icon name="chevron-left" size={16} />
            </button>
          </Tooltip>
          <Tooltip label={step.next === null ? 'Nothing after' : `Next (${formatTime(step.next)})`}>
            <button type="button" className="icon-btn icon-btn--sm" disabled={step.next === null}
              onClick={() => seek(step.next)} aria-label="Next subtitle or annotation">
              <Icon name="chevron-right" size={16} />
            </button>
          </Tooltip>
        </span>
      </div>

      {subs.length === 0 && anns.length === 0 && (
        <p className="current-moment-empty">No subtitle or annotation here yet. Add them below.</p>
      )}

      {subs.length > 0 && (
        <div className="subtitle-list" aria-label="Current subtitle">
          {subs.map((s) => (
            <SubtitleRow
              key={s.id}
              subtitle={s}
              active
              currentTime={currentTime}
              onSelect={onSubtitleSelect}
              onEdited={onSubtitleEdited}
              onDeleted={onSubtitleDeleted}
              onEditingChange={hold(`s:${s.id}`)}
            />
          ))}
          {subs.map((s) => (
            <WordForWordPreview key={`wfw-${s.id}`} subtitle={s} yasna={yasna} language={language} always
              // A gap annotates that word in this subtitle (Requirement 63)
              onAnnotateWord={(text) => setAdding({ start: s.startTime, end: endOf(s), subtitle: true, request: { text, n: Date.now() } })}
              annotations={(bunchList.find((b) => b.subtitle && b.subtitle.id === s.id) || { annotations: [] }).annotations} />
          ))}
        </div>
      )}

      {anns.length > 0 && (
        <ul className="moment-annotator-list" aria-label="Current annotations">
          {anns.map((a) => (
            <AnnotationRow
              key={a.id}
              annotation={a}
              yasna={yasna}
              language={language}
              currentTime={currentTime}
              onUpdated={onAnnotationUpdated}
              onDeleted={onAnnotationDeleted}
              onEditingChange={hold(`a:${a.id}`)}
              reorder={moves[a.id]}
            />
          ))}
        </ul>
      )}

      {adding && (
        <AddWordTo
          target={adding}
          request={adding.request}
          videoId={videoId}
          language={language}
          yasna={yasna}
          yasnaError={yasnaError}
          onSaved={onAnnotationSaved}
          onClose={() => setAdding(null)}
          idSuffix="now"
        />
      )}
    </section>
  );
}

export default CurrentMoment;
